import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import { IReportingPeriodItem } from "../../model/report/ReportingPeriodItem";
import Application from "../../Application";
import GetDataReport1Interactor from "../../interactor/report/GetDataReport1Interactor";
import { IReport1Applicant } from "../../model/report/Report1Applicant";
import { IFormTypeItem } from "../../model/report/FormTypeItem";
import { IFormSection } from "../../model/form/FormSection";
import GetFormTypeListInteractor from "../../interactor/report/GetFormTypeListInteractor";
import { IFormVersionItem } from "../../model/report/FormVersionItem";
import GetReport1FormVersionListInteractor from "../../interactor/report/GetReport1FormVersionListInteractor";
import FormSectionPresentationModel, {
  IFormSectionPresentationModel
} from "../form/model/FormSectionPresentationModel";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import GetReport1ApplicantsInteractor from "../../interactor/report/GetReport1ApplicantsInteractor";
import { ReportConstants } from "../../common/Constants";

export interface TReport1Presenter extends TLoadingAwarePresenter {
  report1ReportingPeriodList: IReportingPeriodItem[];
  report1CurrentReportingPeriod?: IReportingPeriodItem;
  report1?: IFormSectionPresentationModel[];
  report1ApplicantList: IReport1Applicant[];
  report1CurrentApplicant?: IReport1Applicant;
  formTypeList: IFormTypeItem[];
  report1CurrentFormType?: IFormTypeItem;
  report1IncludePublic: boolean;
  formVersionList: IFormVersionItem[];
  report1CurrentFormVersion?: IFormVersionItem;
}

export interface IReport1Presenter extends TReport1Presenter, TPresentable {
  setReportingPeriodList(reportingPeriodList: IReportingPeriodItem[]): void;
  setFormTypeList(formType: IFormTypeItem[]): void;
  onChangeReportingPeriod(reportingPeriodId: number): void;
  onChangeApplicant(applicantId: number): void;
  onChangeFormType(formTypeId: number): void;
  onChangeFormVersion(formVersionId: number): void;
  getPDFReport1(selectedApplicantIds: number[]): Promise<any>;
  onChangeIncludePublic(e: any): void;
  onApplicantSearch(fullName: string): void;
}

const defaultState: TReport1Presenter = {
  report1ReportingPeriodList: [],
  report1ApplicantList: [],
  formTypeList: [],
  formVersionList: [],
  report1IncludePublic: false
};

const Report1Presenter = withStore<IReport1Presenter, TReport1Presenter>(
  ({ application, store, loader, translate }): IReport1Presenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TReport1Presenter>();

    const FormSectionPresentationModelMapper = (
      formSection: IFormSection[]
    ): IFormSectionPresentationModel[] => {
      return formSection.map((section) => {
        return FormSectionPresentationModel(section);
      });
    };

    const setReportingPeriodList = (
      report1ReportingPeriodList: IReportingPeriodItem[]
    ) => {
      _store.update({
        report1ReportingPeriodList
      });
    };

    const setFormTypeList = (formTypeList: IFormTypeItem[]) => {
      _store.update({
        formTypeList
      });
    };

    const onChangeReportingPeriod = async (reportingPeriodId: number) => {
      const report1ReportingPeriodList =
        _store.getState<TReport1Presenter>().report1ReportingPeriodList;
      const report1CurrentReportingPeriod = report1ReportingPeriodList.find(
        (reportingPeriod) => reportingPeriod.id === reportingPeriodId
      );
      const formTypeList = await _application.container
        .resolve<GetFormTypeListInteractor>("getFormTypeList")
        .execute(reportingPeriodId);
      _store.update({
        report1CurrentReportingPeriod,
        report1CurrentFormType: undefined,
        report1CurrentFormVersion: undefined,
        formTypeList,
        report1: undefined
      });
      await getFormVersions();
    };

    const onChangeFormType = async (formTypeId: number) => {
      const formTypeList = _store.getState<TReport1Presenter>().formTypeList;
      const report1CurrentFormType = formTypeList.find(
        (formType) => formType.id.toString() === formTypeId.toString()
      );
      _store.update({
        report1CurrentFormType,
        report1CurrentFormVersion: undefined,
        report1: undefined
      });

      await getFormVersions();
    };

    const onChangeFormVersion = async (formVersionId: number) => {
      const formVersionList =
        _store.getState<TReport1Presenter>().formVersionList;
      const report1CurrentFormVersion = formVersionList.find(
        (formType) => formType.id.toString() === formVersionId.toString()
      );
      _store.update({
        report1CurrentFormVersion,
        report1: undefined
      });
    };

    const onChangeApplicant = async (applicantId: number) => {
      const report1ApplicantList =
        _store.getState<TReport1Presenter>().report1ApplicantList;
      const report1CurrentApplicant = report1ApplicantList.find(
        (applicant) => applicant.id === applicantId
      );
      _store.update({
        report1CurrentApplicant,
        report1CurrentFormVersion: undefined,
        report1: undefined
      });
      await getFormVersions();
    };
    const onChangeIncludePublic = async (e: any) => {
      let report1IncludePublic =
        _store.getState<TReport1Presenter>().report1IncludePublic;
      report1IncludePublic = e.target.checked;
      _store.update({
        report1IncludePublic,
        report1: undefined
      });
    };

    const getFormVersions = async () => {
      const state = _store.getState<TReport1Presenter>();
      if (
        state.report1CurrentApplicant &&
        state.report1CurrentFormType &&
        state.report1CurrentReportingPeriod
      ) {
        const formVersionList = await _application.container
          .resolve<GetReport1FormVersionListInteractor>(
            "getReport1FormVersionList"
          )
          .execute(
            state.report1CurrentReportingPeriod.id,
            state.report1CurrentApplicant.id,
            state.report1CurrentFormType.id
          );
        _store.update({ formVersionList });
      }
    };

    const getPDFReport1 = async () => {
      const state = _store.getState<TReport1Presenter>();
      loader.start("report1Loader");
      let report1;
      let includePublic = false;
      if (state.report1IncludePublic === true) {
        includePublic = true;
      }

      if (state.report1CurrentFormVersion) {
        report1 = await application.container
          .resolve<GetDataReport1Interactor>("getDataReport1")
          .execute(state.report1CurrentFormVersion?.id, includePublic);
      }
      report1 = FormSectionPresentationModelMapper(report1);
      _store.update({
        report1
      });
      loader.stop("report1Loader");
    };

    const onApplicantSearch = async (fullName: string) => {
      try {
        if (
          fullName.length >= ReportConstants.AUTOCOMPLETE.APPLICANT_MIN_LENGTH
        ) {
          let report1ApplicantList = await application.container
            .resolve<GetReport1ApplicantsInteractor>("getReport1Applicants")
            .execute(fullName);
          _store.update({ report1ApplicantList: report1ApplicantList || [] });
        } else {
          _store.update({ report1ApplicantList: [] });
        }
      } catch (error) {
        application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute((error as any).message);
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      setReportingPeriodList,
      onChangeReportingPeriod,
      getPDFReport1,
      setFormTypeList,
      onChangeApplicant,
      onChangeFormType,
      onChangeFormVersion,
      onChangeIncludePublic,
      onApplicantSearch
    };
  },
  defaultState
);

export default Report1Presenter;
